import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Grid, Typography, alpha, Divider } from "@mui/material";
import IdentityVerificationPAN from "./IDVerificationPAN";
import { validateFile, validateFilePNGJPEG } from ".";
import { useSelector } from "react-redux";
import { ContainedButton } from "../../Common/Buttons";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";

const IdentityVerificationDocumentSecond = ({
  handleSteps,
  handleBack,
  userDetails,
  steps,
  payload,
  setIsloading,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isValid },
    reset,
  } = useForm({ mode: "onChange" });
  const { btncolor } = useSelector((state) => state);
  const [payloadSecond, setPayload] = useState({ ...payload });

  const onSubmit = (data) => {
    console.log("Form Data:", data);
    const updatedPayload = {
      idProofFront: data?.idProofFront[0],
      idProofBack: data?.idProofBack[0],
    };
    setPayload((prev) => ({ ...prev, ...updatedPayload }));
    handleSteps();
    reset();
  };

  return (
    <>
      {steps === 6 && (
        <Box sx={card}>
          {/* Step Header */}
          <Box sx={stepHeader}>
            <Box sx={{ ...stepBadge, backgroundColor: alpha(btncolor || "#1976d2", 0.12), color: btncolor }}>
              <BadgeOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={stepTitle}>ID Proof Upload</Typography>
              <Typography sx={stepCaption}>
                Upload the front and back of your government-issued ID
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mb: 3 }} />

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Front */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography sx={fieldLabel}>ID Proof — Front *</Typography>
                <Box sx={uploadCard}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <UploadFileOutlinedIcon sx={{ fontSize: 18, color: btncolor, opacity: 0.8 }} />
                    <Typography sx={{ fontSize: "0.78rem", color: "var(--text-color)", opacity: 0.6 }}>
                      Click to select file
                    </Typography>
                  </Box>
                  <input
                    type="file"
                    style={{ width: "100%", color: "var(--text-color)", fontSize: "0.85rem" }}
                    className={errors.idProofFront ? "is-invalid" : ""}
                    onChange={(e) => {
                      const fileList = e.target.files;
                      setValue("idProofFront", fileList);
                      trigger("idProofFront");
                    }}
                    {...register("idProofFront", {
                      validate: validateFilePNGJPEG,
                    })}
                  />
                </Box>
                {errors.idProofFront && (
                  <Typography sx={errorText}>{errors.idProofFront.message}</Typography>
                )}
                <Typography sx={hintText}>
                  Only government-issued documents (Driver's license, National ID, etc.) are applicable.
                </Typography>
                <Typography sx={hintText}>Accepted: .PNG, .JPEG — MAX 6 MB</Typography>
              </Grid>

              {/* Back */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography sx={fieldLabel}>ID Proof — Back *</Typography>
                <Box sx={uploadCard}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <UploadFileOutlinedIcon sx={{ fontSize: 18, color: btncolor, opacity: 0.8 }} />
                    <Typography sx={{ fontSize: "0.78rem", color: "var(--text-color)", opacity: 0.6 }}>
                      Click to select file
                    </Typography>
                  </Box>
                  <input
                    type="file"
                    style={{ width: "100%", color: "var(--text-color)", fontSize: "0.85rem" }}
                    className={errors.idProofBack ? "is-invalid" : ""}
                    onChange={(e) => {
                      const fileList = e.target.files;
                      setValue("idProofBack", fileList);
                      trigger("idProofBack");
                    }}
                    {...register("idProofBack", {
                      validate: validateFile,
                    })}
                  />
                </Box>
                {errors.idProofBack && (
                  <Typography sx={errorText}>{errors.idProofBack.message}</Typography>
                )}
                <Typography sx={hintText}>
                  Only government-issued documents (Driver's license, National ID, etc.) are applicable.
                </Typography>
                <Typography sx={hintText}>Accepted: .PNG, .JPEG, .DOC, .DOCX, .PDF — MAX 6 MB</Typography>
              </Grid>
            </Grid>

            <Box sx={actionRow}>
              <ContainedButton
                variant="contained"
                onClick={() => handleBack()}
                sx={{ ...submitBtn, opacity: 0.85 }}
              >
                Back
              </ContainedButton>
              <ContainedButton
                type="submit"
                variant="contained"
                sx={submitBtn}
                disabled={!isValid}
              >
                Next
              </ContainedButton>
            </Box>
          </form>
        </Box>
      )}

      {steps > 6 && (
        <IdentityVerificationPAN
          handleSteps={handleSteps}
          handleBack={handleBack}
          userDetails={userDetails}
          steps={steps}
          payload={payloadSecond}
          setIsloading={setIsloading}
        />
      )}
    </>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const card = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  p: { xs: 2.5, md: 3 },
};

const stepHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  mb: 2.5,
  flexWrap: "wrap",
};

const stepBadge = {
  width: 44,
  height: 44,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const stepTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const stepCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.55,
  mt: 0.2,
};

const fieldLabel = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--text-color)",
  opacity: 0.55,
  mb: 0.5,
};

const uploadCard = {
  border: "2px dashed rgba(125,125,125,0.3)",
  borderRadius: "10px",
  p: 1.5,
  mt: 0.5,
  backgroundColor: "var(--body-color-less2)",
};

const hintText = {
  fontSize: "0.72rem",
  color: "var(--text-color)",
  opacity: 0.45,
  mt: 0.75,
  lineHeight: 1.5,
};

const errorText = {
  fontSize: "0.72rem",
  color: "#ef4444",
  mt: 0.5,
};

const actionRow = {
  mt: 3.5,
  pt: 2.5,
  borderTop: "1px solid rgba(125,125,125,0.12)",
  display: "flex",
  gap: 1.5,
};

const submitBtn = {
  height: 48,
  px: 4,
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "0.88rem",
  textTransform: "none",
};

export default IdentityVerificationDocumentSecond;
